import * as React from 'react';
import { useState } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { Alert } from 'reactstrap';
import { logger } from '../logging';
import * as firebaseService from '../services/firebase';
import {
  equalTo,
  get,
  getDatabase,
  orderByChild,
  query,
  ref,
} from 'firebase/database';

type IFBWorkflowFormSelectorProps = {
  formId: string | null;
  setFormId: (id: string) => void;
  disabled?: boolean;
  /** Called when the selected form's published status is known. `null` = no form / still loading. */
  onPublishedChange?: (published: boolean | null) => void;
  /** Called when the selected form title is known. `null` = no form / still loading. */
  onFormTitleChange?: (title: string | null) => void;
};

export const IFBWorkflowFormSelector = ({
  formId,
  setFormId,
  disabled,
  onPublishedChange,
  onFormTitleChange,
}: IFBWorkflowFormSelectorProps) => {
  const ifbApp = firebaseService.getIFBApp();
  const ifbDatabase = getDatabase(ifbApp);

  const [options, setOptions] = useState<any[]>([]);
  const [selectedForm, setSelectedForm] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [unpublishedSelected, setUnpublishedSelected] = useState(false);

  const FORMSREF = 'forms';
  const formsDBRef = ref(ifbDatabase, FORMSREF);

  React.useEffect(() => {
    let isMounted = true;
    if (formId !== null && formId !== '') {
      setIsLoading(true);
      onPublishedChange?.(null);
      onFormTitleChange?.(null);
      const formsQuery = query(formsDBRef, orderByChild('id'), equalTo(formId));
      get(formsQuery).then((snap) => {
        const forms: any[] = [];
        snap.forEach((s) => {
          const val = s.val();
          forms.push(val);
        });

        if (isMounted) {
          const form = forms[0];
          const isPublished = form?.published === true;
          setSelectedForm(forms);
          setUnpublishedSelected(Boolean(form) && !isPublished);
          onPublishedChange?.(form ? isPublished : false);
          onFormTitleChange?.(form?.title || null);
          setIsLoading(false);
        }
      });
    }

    if (formId === null || formId === '') {
      setSelectedForm([]);
      setUnpublishedSelected(false);
      onPublishedChange?.(null);
      onFormTitleChange?.(null);
    }

    return () => {
      isMounted = false;
    };
  }, [formId]);

  const handleSearch = async (queryStr: string) => {
    setIsLoading(true);
    setSelectedForm([]);

    const queryLower = queryStr.toLowerCase();
    const forms: any[] = [];
    const formsQuery = query(
      formsDBRef,
      orderByChild('formType'),
      equalTo('workflow')
    );
    const formsSnap = await get(formsQuery);
    formsSnap.forEach((snap) => {
      const val = snap.val();
      if (val.published !== true) {
        return;
      }
      logger.debug(`form: val.title: ${val.title}`);
      const titleLower = val.title.toLowerCase();
      if (titleLower.includes(queryLower)) {
        forms.push({
          id: val.id,
          title: val.title,
          published: val.published,
          dotmailer: { accountName: val.dotmailer.accountName },
          country: val.country,
        });
      }
    });

    setOptions(forms);
    setIsLoading(false);
  };

  const handleChange = (e) => {
    if (e && e[0] && e[0].id) {
      setSelectedForm(e);
      setUnpublishedSelected(false);
      onPublishedChange?.(true);
      onFormTitleChange?.(e[0].title || null);
      setFormId(e[0].id);
    }
  };

  return (
    <React.Fragment>
      {unpublishedSelected && (
        <Alert color="danger" className="mb-2">
          The selected IFB form is not published. Publish it in IFB or choose a
          published form before saving.
        </Alert>
      )}
      <AsyncTypeahead
        isLoading={isLoading}
        id="ifb-form-selector"
        useCache={false}
        labelKey={(form: any) =>
          `${form.title} - ${form.dotmailer.accountName} [${form.country}]`
        }
        minLength={1}
        onSearch={handleSearch}
        onChange={handleChange}
        placeholder={'Find IFB Workflow Form'}
        selected={selectedForm || []}
        options={options}
        disabled={disabled}
      />
    </React.Fragment>
  );
};
